import {
  useOrganisation,
  useSiteUpdate,
} from "@inrange/building-manager-api-client";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import { Site } from "@inrange/building-manager-api-client/models-site";
import { useState } from "react";
import { Alert, Button, Form, InputGroup, Table } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";

interface SPV {
  uuid: string;
  name: string;
}

const OrgSPVs = ({
  organisation,
  sites,
  spvsAlertStyle,
  setSpvsAlertStyle,
  spvsAlertMessage,
  setSpvsAlertMessage,
}: {
  organisation: Organisation;
  sites: Site[];
  spvsAlertStyle: string;
  setSpvsAlertStyle: (style: string) => void;
  spvsAlertMessage: string | undefined;
  setSpvsAlertMessage: (message: string | undefined) => void;
}) => {
  const { updateOrganisation, invalidateOrg } = useOrganisation(
    organisation.id
  );
  const siteUpdate = useSiteUpdate();
  const queryClient = useQueryClient();
  const [newSPVName, setNewSPVName] = useState<string>("");
  const [spvsIsRemoving, setSpvsIsRemoving] = useState<{
    [key: string]: boolean;
  }>({});
  const [editingSPV, setEditingSPV] = useState<string | null>(null);
  const [editedName, setEditedName] = useState<string>("");

  const onAddSPV = (name: string) => {
    const newSPV: SPV = {
      uuid: crypto.randomUUID(),
      name: name.trim(),
    };
    updateOrganisation.mutate(
      {
        organisation: {
          spvs: [...(organisation.spvs || []), newSPV],
        },
      },
      {
        onError: (error: any) => {
          alert(error?.response?.data?.message || error);
        },
        onSuccess: () => {
          setSpvsAlertStyle("success");
          setSpvsAlertMessage(`Successfully added SPV "${newSPV.name}"`);
        },
      }
    );
  };

  const onRenameSPV = (uuid: string, newName: string) => {
    const updatedSPVs = (organisation.spvs || []).map((spv) =>
      spv.uuid === uuid ? { ...spv, name: newName.trim() } : spv
    );

    updateOrganisation.mutate(
      {
        organisation: {
          spvs: updatedSPVs,
        },
      },
      {
        onError: (error: any) => {
          alert(error?.response?.data?.message || error);
        },
        onSuccess: () => {
          setEditingSPV(null);
          setEditedName("");
          setSpvsAlertStyle("success");
          setSpvsAlertMessage(`Successfully renamed SPV to "${newName}"`);
        },
      }
    );
  };

  const onRemoveSPV = async (uuid: string) => {
    setSpvsIsRemoving({ ...spvsIsRemoving, [uuid]: true });
    updateOrganisation.mutate(
      {
        organisation: {
          spvs: (organisation.spvs || []).filter((spv) => spv.uuid !== uuid),
        },
      },
      {
        onError: (error: any) => {
          setSpvsIsRemoving({ ...spvsIsRemoving, [uuid]: false });
          alert(error?.response?.data?.message || error);
        },
        onSuccess: () => {
          setSpvsIsRemoving({ ...spvsIsRemoving, [uuid]: false });
        },
      }
    );

    const sitesToUpdate = sites.filter((site) =>
      site.siteOwnerships.some((so) => so.spv === uuid)
    );

    if (sitesToUpdate.length === 0) {
      setSpvsAlertMessage(undefined);
      return;
    }

    setSpvsAlertStyle("secondary");
    setSpvsAlertMessage(`Updating ${sitesToUpdate.length} sites...`);

    const mutations = sitesToUpdate.map((site) =>
      siteUpdate.mutateAsync({
        siteId: site.id,
        payload: {
          newOwnerships: site.siteOwnerships
            .filter((so) => so.spv === uuid)
            .map((so) => {
              so.spv = undefined;
              return so;
            }),
        },
      })
    );

    try {
      const results = await Promise.allSettled(mutations);
      queryClient.invalidateQueries(["sites"]);
      for (const site of sitesToUpdate) {
        queryClient.invalidateQueries(["site", site.id]);
      }
      const successfulUpdates = results.filter(
        (result) => result.status === "fulfilled"
      ).length;
      const failedUpdates = results.filter(
        (result) => result.status === "rejected"
      ).length;

      if (failedUpdates === 0) {
        setSpvsAlertStyle("success");
        setSpvsAlertMessage(`Successfully updated ${successfulUpdates} sites`);
      } else {
        setSpvsAlertStyle("danger");
        setSpvsAlertMessage(
          `Failed to update ${failedUpdates} out of ${sitesToUpdate.length} sites`
        );
      }
    } catch (error) {
      setSpvsAlertStyle("danger");
      setSpvsAlertMessage(`An unexpected error occurred while updating sites`);
    }
    invalidateOrg();
  };

  const addSPV = (name: string) => {
    onAddSPV(name);
    setNewSPVName("");
  };

  const startEdit = (spv: SPV) => {
    setEditingSPV(spv.uuid);
    setEditedName(spv.name);
  };

  const cancelEdit = () => {
    setEditingSPV(null);
    setEditedName("");
  };

  const saveEdit = (uuid: string) => {
    if (editedName.trim()) {
      onRenameSPV(uuid, editedName);
    }
  };

  const spvs = organisation.spvs || [];

  return (
    <>
      {spvsAlertMessage && (
        <Alert
          variant={spvsAlertStyle}
          onClose={() => setSpvsAlertMessage(undefined)}
          dismissible={spvsAlertStyle !== "secondary"}
        >
          <div>{spvsAlertMessage}</div>
        </Alert>
      )}
      <div>
        <h4>Manage SPVs ({spvs.length})</h4>
        {spvs.length > 0 && (
          <>
            <Table bordered responsive style={{ fontSize: 14 }}>
              <thead>
                <tr>
                  <th>SPV Name</th>
                  <th>Number of sites</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {[...spvs]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((spv) => (
                    <tr key={spv.uuid}>
                      <td>
                        {editingSPV === spv.uuid ? (
                          <Form.Control
                            type="text"
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                saveEdit(spv.uuid);
                              } else if (e.key === "Escape") {
                                cancelEdit();
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          spv.name
                        )}
                      </td>
                      <td>
                        {
                          sites.filter((site) =>
                            site.siteOwnerships.some(
                              (so) => so.spv === spv.uuid
                            )
                          ).length
                        }
                      </td>
                      <td>
                        {editingSPV === spv.uuid ? (
                          <>
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => saveEdit(spv.uuid)}
                              disabled={!editedName.trim()}
                              style={{ marginRight: 8 }}
                            >
                              Save
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={cancelEdit}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => startEdit(spv)}
                              style={{ marginRight: 8 }}
                            >
                              Rename
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => onRemoveSPV(spv.uuid)}
                              disabled={spvsIsRemoving[spv.uuid]}
                            >
                              Remove
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </>
        )}

        <InputGroup className="mb-3">
          <Form.Control
            placeholder="SPV name"
            type="text"
            value={newSPVName}
            onChange={(e) => setNewSPVName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newSPVName.trim()) {
                addSPV(newSPVName);
              }
            }}
          />
          <Button
            variant="primary"
            onClick={() => addSPV(newSPVName)}
            style={{ marginRight: 10 }}
            disabled={newSPVName.trim().length === 0}
          >
            Add SPV
          </Button>
        </InputGroup>
      </div>
    </>
  );
};

export default OrgSPVs;
